import { useState, useEffect, useCallback, useRef } from 'react';
import { SettingsAPI } from '../utils/api';
import { useAuth } from '../components/AuthProvider';
import { isTransientNetworkError } from '../utils/auth';
import { setDesktopNotificationsPref } from '../utils/desktopNotify';
import { mergeSettingsPreservingTabs } from '../utils/mergeSettingsPreservingTabs';

/**
 * useUserSettings Hook
 * 
 * Manages user settings stored on the server, including:
 * - sprintOrder: Array of sprint IDs defining sidebar order
 * - collapsedSprints: Array of collapsed sprint IDs (not currently used)
 * - preferences: User preferences like theme, defaultView
 * 
 * Settings are loaded on mount and saved automatically when changed.
 * Uses debouncing to prevent excessive API calls during rapid changes.
 */
// Default visibility settings for flow view HUD elements
const DEFAULT_FLOW_VISIBILITY = {
  activeMissions: true,
  availableMissions: true,
  milestones: true,
  minimap: true,
  campaignsKey: true,
};


const buildDefaultSettings = () => ({
  sprintOrder: [],
  collapsedSprints: [],
  lastSelectedCampaignId: null,
  preferences: {
    theme: 'dark',
    defaultView: 'flow',
    // Trackpad/Mouse sensitivity
    trackpadPanSensitivity: 1.0,
    trackpadZoomSensitivity: 1.0,
    // Auto-archive settings
    autoArchiveEnabled: true,
    autoArchiveDays: 7,
    desktopNotifications: true,
  },
  flowViewVisibility: DEFAULT_FLOW_VISIBILITY,
  hideViewExplainerModal: false,
});

export function useUserSettings() {
  const { workspaceId } = useAuth();
  const [settings, setSettings] = useState(buildDefaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Which workspace the current in-memory settings belong to
  // (also prevents double-load in strict mode)
  const loadedForWorkspaceRef = useRef(undefined);
  // Skip saves until a load has finished — otherwise a workspace switch
  // resets to defaults and can PUT an empty openTabs over a good tab set.
  const hydratedRef = useRef(false);
  
  // Debounce timer + pending payload for saving. The pending payload carries
  // the workspace it belongs to, so a save that fires after a workspace switch
  // still writes to the right partition.
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(null);
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;

  /** Push any pending debounced save immediately (used on workspace switch). */
  const flushPendingSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingSaveRef.current;
    if (!pending) return;
    pendingSaveRef.current = null;
    SettingsAPI.get({ workspaceId: pending.workspaceId })
      .then((current) => SettingsAPI.save(
        mergeSettingsPreservingTabs(current, pending.settings),
        { workspaceId: pending.workspaceId }
      ))
      .catch((err) => {
        console.error('[useUserSettings] Failed to flush settings:', err);
      });
  }, []);

  // Load settings on mount and whenever the active workspace changes —
  // settings (sprintOrder, lastSelectedCampaignId, preferences…) are stored
  // per workspace on the backend and must never carry over between workspaces.
  useEffect(() => {
    if (loadedForWorkspaceRef.current === workspaceId) return;
    const isWorkspaceSwitch = loadedForWorkspaceRef.current !== undefined;
    loadedForWorkspaceRef.current = workspaceId;

    if (isWorkspaceSwitch) {
      // Persist unsaved changes to the workspace they came from, then reset
      flushPendingSave();
      hydratedRef.current = false;
      setSettings(buildDefaultSettings());
    }
    
    const loadSettings = async () => {
      setLoading(true);
      for (;;) {
        let retry = false;
        try {
        const loadedSettings = await SettingsAPI.get({ workspaceId });
        // A later workspace switch superseded this load — drop the response
        if (loadedForWorkspaceRef.current !== workspaceId) return;
        
        // Migrate localStorage settings if backend doesn't have them yet
        let migratedPreferences = loadedSettings.preferences || {};
        if (!migratedPreferences.autoArchiveEnabled && !migratedPreferences.trackpadPanSensitivity) {
          try {
            const localStorageSettings = localStorage.getItem('plet-settings');
            if (localStorageSettings) {
              const parsed = JSON.parse(localStorageSettings);
              console.log('[useUserSettings] Migrating localStorage settings to backend:', parsed);
              migratedPreferences = {
                ...migratedPreferences,
                trackpadPanSensitivity: parsed.trackpadPanSensitivity ?? 1.0,
                trackpadZoomSensitivity: parsed.trackpadZoomSensitivity ?? 1.0,
                autoArchiveEnabled: parsed.autoArchiveEnabled ?? true,
                autoArchiveDays: parsed.autoArchiveDays ?? 7,
              };
            }
          } catch (e) {
            console.warn('[useUserSettings] Failed to migrate localStorage settings:', e);
          }
        }
        
        // Merge loaded settings with defaults to ensure new fields have values
        const mergedSettings = {
          sprintOrder: [],
          collapsedSprints: [],
          lastSelectedCampaignId: null,
          ...loadedSettings,
          preferences: {
            theme: 'dark',
            defaultView: 'flow',
            trackpadPanSensitivity: 1.0,
            trackpadZoomSensitivity: 1.0,
            autoArchiveEnabled: true,
            autoArchiveDays: 7,
            ...migratedPreferences,
          },
          // Ensure flowViewVisibility merges properly (don't lose defaults for new keys)
          flowViewVisibility: {
            ...DEFAULT_FLOW_VISIBILITY,
            ...(loadedSettings.flowViewVisibility || {}),
          },
          // Ensure hideViewExplainerModal has a default
          hideViewExplainerModal: loadedSettings.hideViewExplainerModal ?? false,
        };
        
        setSettings(mergedSettings);
        hydratedRef.current = true;
        
        // If we migrated settings, save them to backend
        if (migratedPreferences.autoArchiveEnabled !== undefined && 
            !loadedSettings.preferences?.autoArchiveEnabled) {
          console.log('[useUserSettings] Saving migrated settings to backend');
          const current = await SettingsAPI.get({ workspaceId });
          await SettingsAPI.save(
            mergeSettingsPreservingTabs(current, mergedSettings),
            { workspaceId }
          );
        }
        
        setError(null);
      } catch (err) {
        if (loadedForWorkspaceRef.current !== workspaceId) return;
        if (isTransientNetworkError(err)) {
          console.warn('[useUserSettings] Load failed while offline, retrying:', err);
          retry = true;
        } else {
          console.error('[useUserSettings] Failed to load settings:', err);
          setError(err.message);
          // Keep default settings on error
          return;
        }
      } finally {
        if (!retry && loadedForWorkspaceRef.current === workspaceId) {
          setLoading(false);
        }
      }
      if (!retry) return;
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (loadedForWorkspaceRef.current !== workspaceId) return;
      }
    };
    
    loadSettings();
  }, [workspaceId, flushPendingSave]);

  // Save settings to server (debounced, pinned to the current workspace)
  const saveSettings = useCallback(async (newSettings) => {
    if (!hydratedRef.current) return;

    pendingSaveRef.current = {
      settings: newSettings,
      workspaceId: workspaceIdRef.current,
    };

    // Clear any pending save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // Debounce the save by 500ms
    saveTimerRef.current = setTimeout(async () => {
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = null;
      saveTimerRef.current = null;
      if (!pending) return;
      try {
        console.log('[useUserSettings] Saving settings:', pending.settings);
        const current = await SettingsAPI.get({ workspaceId: pending.workspaceId });
        await SettingsAPI.save(
          mergeSettingsPreservingTabs(current, pending.settings),
          { workspaceId: pending.workspaceId }
        );
        console.log('[useUserSettings] Settings saved successfully');
      } catch (err) {
        console.error('[useUserSettings] Failed to save settings:', err);
        // Don't update error state for save failures - settings are still in memory
      }
    }, 500);
  }, []);

  // Update sprint order
  const setSprintOrder = useCallback((newOrder) => {
    setSettings(prev => {
      const updated = {
        ...prev,
        sprintOrder: newOrder
      };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  // Update a single sprint's position in the order
  const moveSprintInOrder = useCallback((sprintId, newIndex) => {
    setSettings(prev => {
      const currentOrder = [...prev.sprintOrder];
      const currentIndex = currentOrder.indexOf(sprintId);
      
      // Remove from current position if exists
      if (currentIndex !== -1) {
        currentOrder.splice(currentIndex, 1);
      }
      
      // Insert at new position
      currentOrder.splice(newIndex, 0, sprintId);
      
      const updated = {
        ...prev,
        sprintOrder: currentOrder
      };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  // Add a new sprint to the order (at the beginning by default)
  const addSprintToOrder = useCallback((sprintId, atBeginning = true) => {
    setSettings(prev => {
      // Don't add if already in order
      if (prev.sprintOrder.includes(sprintId)) {
        return prev;
      }
      
      const newOrder = atBeginning 
        ? [sprintId, ...prev.sprintOrder]
        : [...prev.sprintOrder, sprintId];
      
      const updated = {
        ...prev,
        sprintOrder: newOrder
      };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  // Remove a sprint from the order
  const removeSprintFromOrder = useCallback((sprintId) => {
    setSettings(prev => {
      const updated = {
        ...prev,
        sprintOrder: prev.sprintOrder.filter(id => id !== sprintId)
      };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  // Set the last selected campaign ID
  const setLastSelectedCampaignId = useCallback((campaignId) => {
    setSettings(prev => {
      // Don't update if it's the same
      if (prev.lastSelectedCampaignId === campaignId) {
        return prev;
      }
      const updated = {
        ...prev,
        lastSelectedCampaignId: campaignId
      };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  // Update flow view visibility settings
  const setFlowViewVisibility = useCallback((newVisibility) => {
    setSettings(prev => {
      const updated = {
        ...prev,
        flowViewVisibility: {
          ...DEFAULT_FLOW_VISIBILITY,
          ...prev.flowViewVisibility,
          ...newVisibility,
        },
      };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  // Update hide view explainer modal preference
  const setHideViewExplainerModal = useCallback((hide) => {
    setSettings(prev => {
      const updated = {
        ...prev,
        hideViewExplainerModal: hide,
      };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  // Update user preferences (theme, trackpad sensitivity, auto-archive, etc.)
  const updatePreferences = useCallback((updates) => {
    setSettings(prev => {
      const updated = {
        ...prev,
        preferences: {
          ...prev.preferences,
          ...updates,
        },
      };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setDesktopNotificationsPref(settings.preferences?.desktopNotifications !== false);
  }, [settings.preferences?.desktopNotifications]);

  return {
    settings,
    loading,
    error,
    sprintOrder: settings.sprintOrder,
    setSprintOrder,
    moveSprintInOrder,
    addSprintToOrder,
    removeSprintFromOrder,
    lastSelectedCampaignId: settings.lastSelectedCampaignId,
    setLastSelectedCampaignId,
    flowViewVisibility: { ...DEFAULT_FLOW_VISIBILITY, ...settings.flowViewVisibility },
    setFlowViewVisibility,
    hideViewExplainerModal: settings.hideViewExplainerModal || false,
    setHideViewExplainerModal,
    preferences: settings.preferences,
    updatePreferences,
  };
}

export default useUserSettings;
